import { Autocomplete, TextField, CircularProgress, Box } from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { useMemo } from "react";

const CREATE_OPTION_ID = "__create__";

export interface CreateOption {
  id: typeof CREATE_OPTION_ID;
  __isCreate: true;
  label: string;
}

function isCreateOption<T>(option: T | CreateOption): option is CreateOption {
  return (option as CreateOption)?.__isCreate === true;
}

export interface AsyncAutocompleteWithQuickCreateProps<T extends { id: string }> {
  value: string;
  onChange: (id: string | null) => void;
  options: T[];
  getOptionLabel: (option: T) => string;
  label: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
  /** Show "Create new X" option; set false for filter dropdowns */
  showCreate?: boolean;
  /** Label for the "Create new" option (required when showCreate is true) */
  createLabel?: string;
  /** Called when user selects the create option (required when showCreate is true) */
  onOpenQuickCreate?: () => void;
  /** When using server-side search, call to drive search term (debounce in parent/hook) */
  onInputChange?: (event: unknown, value: string) => void;
  /** When using server-side search, pass (x) => x to disable client-side filtering */
  filterOptions?: (options: (T | CreateOption)[]) => (T | CreateOption)[];
  /** Called when dropdown opens (use to set menuOpen for lazy fetch) */
  onOpen?: () => void;
  /** Called when dropdown closes */
  onClose?: () => void;
  /** Text when no options and not loading (default: "Type to search" when searchable, else "No options") */
  noOptionsText?: string;
  error?: string;
  helperText?: string;
}

/**
 * Autocomplete that supports search + a "Create new X" option.
 * When user selects the create option, onOpenQuickCreate() is called; parent opens modal and on success sets new entity as selected.
 */
export default function AsyncAutocompleteWithQuickCreate<T extends { id: string }>({
  value,
  onChange,
  options,
  getOptionLabel,
  label,
  placeholder,
  required = false,
  disabled = false,
  loading = false,
  showCreate = true,
  createLabel = "",
  onOpenQuickCreate,
  onInputChange,
  filterOptions: filterOptionsProp,
  onOpen,
  onClose,
  noOptionsText,
  error,
  helperText,
}: AsyncAutocompleteWithQuickCreateProps<T>) {
  const createOption: CreateOption = useMemo(
    () => ({ id: CREATE_OPTION_ID, __isCreate: true, label: createLabel }),
    [createLabel]
  );

  const allOptions = useMemo(
    () => (showCreate ? [...options, createOption] : options),
    [options, createOption, showCreate]
  );

  const effectiveNoOptionsText =
    noOptionsText ?? (onInputChange ? "Type to search or select" : "No options");

  const selectedOption = useMemo(() => {
    if (!value) return null;
    if (value === CREATE_OPTION_ID) return null;
    return options.find((o) => o.id === value) ?? null;
  }, [options, value]);

  const handleChange = (_: unknown, newValue: T | CreateOption | null) => {
    if (showCreate && newValue && isCreateOption(newValue)) {
      onOpenQuickCreate?.();
      return;
    }
    onChange(newValue ? (newValue as T).id : null);
  };

  return (
    <Autocomplete<T | CreateOption>
      value={selectedOption as T | null}
      onChange={handleChange}
      onInputChange={onInputChange}
      onOpen={onOpen}
      onClose={onClose}
      options={allOptions}
      getOptionLabel={(option) =>
        isCreateOption(option) ? option.label : getOptionLabel(option as T)
      }
      filterOptions={filterOptionsProp ?? undefined}
      loading={loading}
      disabled={disabled}
      noOptionsText={loading ? "Loading…" : effectiveNoOptionsText}
      isOptionEqualToValue={(option, val) => {
        if (isCreateOption(option)) return false;
        return (option as T).id === (val as T)?.id;
      }}
      renderOption={(props, option) => {
        if (showCreate && isCreateOption(option)) {
          return (
            <Box
              component="li"
              {...props}
              key={CREATE_OPTION_ID}
              sx={{ borderTop: 1, borderColor: "divider", fontWeight: 600 }}
            >
              <AddOutlinedIcon sx={{ mr: 1, fontSize: 20 }} />
              {option.label}
            </Box>
          );
        }
        return (
          <Box component="li" {...props} key={(option as T).id}>
            {getOptionLabel(option as T)}
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder ?? (onInputChange ? "Type to search…" : undefined)}
          required={required}
          error={!!error}
          helperText={error || helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
